import React, { useState, useEffect } from 'react';
import { FaHeart, FaCheck, FaRupeeSign } from 'react-icons/fa';

const Donate = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const predefinedAmounts = [500, 1000, 2500, 5000, 10000];

  const handleInputChange = (e) => {
    setDonorInfo({ ...donorInfo, [e.target.name]: e.target.value });
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleDonate = async () => {
    const amount = customAmount || selectedAmount;
    
    if (!amount || amount <= 0) {
      alert('Please select or enter a valid donation amount');
      return;
    }

    if (!donorInfo.name || !donorInfo.email || !donorInfo.phone) {
      alert('Please fill in all required fields');
      return;
    }

    const res = await loadRazorpay();

    if (!res) {
      alert('Razorpay SDK failed to load. Please check your internet connection.');
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: amount * 100, // Amount in paise
      currency: 'INR',
      name: 'Helen Keller Society',
      description: 'Supporting individuals with disabilities',
      image: 'https://via.placeholder.com/150?text=HKS',
      handler: function (response) {
        alert(`Thank you for your donation! Payment ID: ${response.razorpay_payment_id}`);
        // Reset form
        setSelectedAmount(null);
        setCustomAmount('');
        setDonorInfo({ name: '', email: '', phone: '', message: '' });
      },
      prefill: {
        name: donorInfo.name,
        email: donorInfo.email,
        contact: donorInfo.phone,
      },
      notes: {
        message: donorInfo.message,
      },
      theme: {
        color: '#FF6B35',
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  const impactAreas = [
    {
      title: 'Education',
      description: 'Sponsor a child\'s education for a year',
      amount: '₹10,000',
      icon: '📚',
    },
    {
      title: 'Healthcare',
      description: 'Provide therapy sessions for 3 months',
      amount: '₹5,000',
      icon: '🏥',
    },
    {
      title: 'Training',
      description: 'Support vocational training program',
      amount: '₹15,000',
      icon: '💼',
    },
    {
      title: 'Assistive Devices',
      description: 'Help provide mobility aids',
      amount: '₹25,000',
      icon: '♿',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-success to-accent text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <FaHeart className="text-6xl mx-auto mb-6 animate-pulse" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="donate-hero-title">
              Make a Difference Today
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-95">
              Your generosity transforms lives and creates opportunities for individuals with disabilities
            </p>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-16 bg-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4" data-testid="impact-title">
              Your Donation Creates Impact
            </h2>
            <p className="text-gray-600 text-lg">
              See how your contribution makes a real difference
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {impactAreas.map((area, index) => (
              <div
                key={index}
                data-testid={`impact-card-${index}`}
                className="bg-white rounded-xl p-6 text-center shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="text-5xl mb-4">{area.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{area.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{area.description}</p>
                <p className="text-primary font-bold text-2xl">{area.amount}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-warm to-white rounded-2xl shadow-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center" data-testid="donation-form-title">
              Complete Your Donation
            </h2>

            {/* Amount Selection */}
            <div className="mb-8">
              <label className="block text-gray-700 font-semibold mb-4 text-lg">
                Select Donation Amount
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {predefinedAmounts.map((amount) => (
                  <button
                    key={amount}
                    data-testid={`amount-${amount}`}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={`${
                      selectedAmount === amount
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                    } border-2 rounded-lg py-4 px-4 font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center`}
                  >
                    {selectedAmount === amount && (
                      <FaCheck className="mr-2" />
                    )}
                    <FaRupeeSign className="text-sm mr-1" />
                    {amount}
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  Or Enter Custom Amount
                </label>
                <div className="relative">
                  <FaRupeeSign className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    data-testid="custom-amount-input"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Donor Information */}
            <div className="space-y-4 mb-8">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  data-testid="donor-name-input"
                  value={donorInfo.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none transition-all"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    data-testid="donor-email-input"
                    value={donorInfo.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    data-testid="donor-phone-input"
                    value={donorInfo.phone}
                    onChange={handleInputChange}
                    placeholder="+91 98765 43210"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Message (Optional)
                </label>
                <textarea
                  name="message"
                  data-testid="donor-message-input"
                  value={donorInfo.message}
                  onChange={handleInputChange}
                  placeholder="Share why you're supporting us..."
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary focus:outline-none transition-all"
                />
              </div>
            </div>

            {/* Donate Button */}
            <button
              onClick={handleDonate}
              data-testid="proceed-donate-btn"
              className="w-full bg-gradient-to-r from-primary to-secondary text-white py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
            >
              <FaHeart />
              <span>Proceed to Donate</span>
            </button>

            <p className="text-center text-gray-500 text-sm mt-4">
              Secure payment powered by Razorpay
            </p>
          </div>
        </div>
      </section>

      {/* Why Donate */}
      <section className="py-16 bg-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Why Your Support Matters
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Direct Impact</h3>
              <p className="text-gray-600">
                100% of your donation goes directly to programs supporting individuals with disabilities
              </p>
            </div>
            <div className="text-center">
              <div className="text-5xl mb-4">🔒</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Secure & Transparent</h3>
              <p className="text-gray-600">
                All transactions are secure and we maintain complete transparency in fund utilization
              </p>
            </div>
            <div className="text-center">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Regular Updates</h3>
              <p className="text-gray-600">
                Receive updates on how your contribution is creating positive change
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-primary to-secondary text-white rounded-2xl p-12 shadow-2xl">
            <p className="text-2xl italic mb-6">
              "Thanks to the support of donors like you, my daughter now has the skills and confidence 
              to live independently. Your generosity has given our family hope and a brighter future."
            </p>
            <p className="font-bold text-xl">- Meena Patel, Parent</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate